import type {ReactElement} from 'react';
import {useEffect, useRef, useState} from 'react';
import {Button, IconPlacement, ButtonSize} from '@Components/button';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import {getAssetUrl} from '@Utils/s3.util';
import type {ConfirmationCarouselCard} from '@Components/checkout-flow/components/confirmation-carousel/confirmation-carousel.types';
import {CAROUSEL_SCROLL_EDGE_TOLERANCE_PX, getConfirmationCarouselCards} from '@Components/checkout-flow/components/confirmation-carousel/confirmation-carousel.types';
import styles from './confirmation-carousel.module.scss';

export function ConfirmationCarousel(): ReactElement {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isAtStart, setIsAtStart] = useState(true);
  const [isAtEnd, setIsAtEnd] = useState(false);
  const cards = getConfirmationCarouselCards();

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container === null) {
      return undefined;
    }
    const updateEdges = (): void => {
      const maxScrollLeft = container.scrollWidth - container.clientWidth;
      setIsAtStart(container.scrollLeft <= CAROUSEL_SCROLL_EDGE_TOLERANCE_PX);
      setIsAtEnd(Math.abs(container.scrollLeft - maxScrollLeft) <= CAROUSEL_SCROLL_EDGE_TOLERANCE_PX);
    };
    updateEdges();
    container.addEventListener('scroll', updateEdges);
    return (): void => {
      container.removeEventListener('scroll', updateEdges);
    };
  }, []);

  const scrollTo = (toStart: boolean): void => {
    const container = scrollContainerRef.current;
    if (container === null) {
      return;
    }
    container.scrollTo({left: toStart ? 0 : container.scrollWidth - container.clientWidth, behavior: 'smooth'});
  };

  const getArrow = (isBack: boolean): ReactElement | null => {
    if ((isBack && isAtStart) || (!isBack && isAtEnd)) {
      return null;
    }
    return (
      <div className={`${styles.arrowContainer} ${isBack ? styles.arrowBack : styles.arrowForward}`}>
        <Icon
          icon={isBack ? 'icon-arrow-left' : 'icon-arrow-right'}
          type={IconType.SECONDARY}
          shape={IconShape.CIRCLE}
          size={IconSize.SIZE_ICON_24}
          className={styles.arrow}
          onClick={(): void => {
            scrollTo(isBack);
          }}
          isUsedAsButton
          ariaLabel={window.i18next.t(isBack ? 'pmwjs_back' : 'pmwjs_next')}
        />
      </div>
    );
  };

  const getCard = (card: ConfirmationCarouselCard): ReactElement => {
    return (
      <div key={card.title} className={`${styles.card} ${styles[card.backgroundClass]}`}>
        <div className={styles.cardBody}>
          <Text val={card.title} size={TextSize.LG} bold className={`content-heading ${styles.cardTitle}`} />
          <Text val={card.subtext} size={TextSize.SM} className={`content-body ${styles.cardSubtext}`} />
          <Button
            text={card.ctaText}
            icon={card.ctaIcon}
            iconPlacement={IconPlacement.LEFT}
            size={ButtonSize.MEDIUM}
            {...card.ctaAppearance}
            href={window.PMW.util.site_url(card.ctaUri)}
            customClasses={`${styles.cardCta} ${card.ctaClassName ?? ''}`}
          />
        </div>
        <img src={getAssetUrl(card.imageFileName)} alt="" className={styles.cardImage} />
      </div>
    );
  };

  return (
    <div className={styles.carouselWrapper}>
      {getArrow(true)}
      <div ref={scrollContainerRef} className={styles.scrollContainer}>
        <div className={styles.track}>
          {cards.map(getCard)}
          <div className={styles.scrollAnchor} />
        </div>
      </div>
      {getArrow(false)}
    </div>
  );
}
